import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import * as actions from '../actions';
import * as c from '../constant';
import StepProgress from '../components/StepProgress';

const CATEGORY_META = {
  citizens_charter: { title: "Understanding the Process", subtitle: "(Citizen's Charter)" },
  service_quality: { title: 'Service Quality Dimension', subtitle: '' },
  system_usability: { title: `eLGU System's Instability, Learnability, and  Understandability`, subtitle: '' },
  customer_experience: { title: `eLGU System's Look and Feel`, subtitle: '' },
  system_efficiency_and_effectiveness: {
    title: `eLGU System's Efficiency and Effectiveness`,
    subtitle: '',
  },
  system_cloud_based: { title: `The eLGU System's Cloud-based Functionality`, subtitle: '' },
  promoting_system: { title: 'Promoting the eLGU System', subtitle: '' },
};

const CATEGORY_ORDER = [
  'citizens_charter', // CC*
  'service_quality', // SQD*
];

const sectionTitle = (category) => {
  const meta = CATEGORY_META[category];
  if (meta?.title) return meta.title;
  return String(category || 'feedback');
};

const sectionSubtitle = (category) => {
  const meta = CATEGORY_META[category];
  if (meta?.subtitle) return meta.subtitle;
  return '';
};

const buildSectionsFromQuestions = (list) => {
  const questions = Array.isArray(list)
    ? list
    : _.get(list, 'data') || _.get(list, 'items') || _.get(list, 'questions') || [];
  const grouped = _.groupBy(questions, (q) => _.get(q, 'category') || 'general');

  const sections = Object.keys(grouped).map((category) => {
    const qs = grouped[category] || [];
    const sorted = _.sortBy(qs, [(q) => _.get(q, 'order', 9999), (q) => _.get(q, 'id', 9999)]);
    return {
      category,
      title: sectionTitle(category),
      subtitle: sectionSubtitle(category),
      questions: sorted,
      minOrder: _.min(sorted.map((q) => _.get(q, 'order', 9999))) ?? 9999,
    };
  });

  return _.sortBy(sections, [
    (s) => {
      const idx = CATEGORY_ORDER.indexOf(s.category);
      return idx === -1 ? 999 : idx;
    },
    'minOrder',
    'category',
  ]);
};

const questionKey = (q) => `q_${_.get(q, 'id')}`;

const questionText = (q) =>
  _.get(q, 'question_text') || _.get(q, 'question') || '';

const questionSubText = (q) => _.get(q, 'filipino_translation') || '';

const normalizeChoices = (choices) => {
  const arr = Array.isArray(choices) ? choices : [];
  return _.sortBy(arr, [(choice) => _.get(choice, 'order', 9999), (choice) => _.get(choice, 'id', 9999)]).map((choice) => ({
    id: _.get(choice, 'id'),
    label: _.get(choice, 'choice_text') ?? _.get(choice, 'label') ?? '',
    value: _.get(choice, 'choice_value') === null ? '__NULL__' : String(_.get(choice, 'choice_value')),
  }));
};

const isConditionMet = ({ logic, dependsOnAnswer }) => {
  if (!logic) return true;
  const cond = _.get(logic, 'condition');
  const values = _.get(logic, 'values') || [];
  const dependsNum = Number(dependsOnAnswer);
  const hasNumericValues = Array.isArray(values) && values.some((v) => typeof v === 'number');
  const normalizedDepends = hasNumericValues && !Number.isNaN(dependsNum) ? dependsNum : dependsOnAnswer;

  if (cond === 'in') return values.includes(normalizedDepends);
  if (cond === 'equals') return normalizedDepends === _.get(values, 0);
  return true;
};

function FeedbackQuestionsModal({
  onClose,
  initialFormData,
  getFeedbackFormsCore,
  feedbackFormsCore,
  onSubmitted,
  onBack,
}) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(() => ({
    ...(initialFormData || {}),
  }));

  useEffect(() => {
    if (!feedbackFormsCore) getFeedbackFormsCore();
  }, [feedbackFormsCore, getFeedbackFormsCore]);

  const sections = useMemo(
    () => buildSectionsFromQuestions(feedbackFormsCore),
    [feedbackFormsCore]
  );
  const currentSection = sections[step] || null;
  const questions = useMemo(() => _.get(currentSection, 'questions') || [], [currentSection]);

  const onChangeRadio = (e) => {
    const { name, value } = e.target;
    setAnswers((prev) => ({ ...prev, [name]: value }));
  };

  const onChangeText = (e) => {
    const { name, value } = e.target;
    setAnswers((prev) => ({ ...prev, [name]: value }));
  };

  const isCurrentSectionValid = useMemo(() => {
    return () => {
      return questions.every((q) => {
        const key = questionKey(q);
        const type = _.get(q, 'question_type');
        const required = !!_.get(q, 'is_required');
        const logic = _.get(q, 'conditional_logic');
        const dependsOnId = _.get(logic, 'depends_on');
        const dependsKey = dependsOnId ? `q_${dependsOnId}` : null;
        const dependsOnAnswer = dependsKey ? _.get(answers, dependsKey) : undefined;
        const conditionMet = logic ? isConditionMet({ logic, dependsOnAnswer }) : true;

        // If the question is not visible due to condition logic, skip validation
        if (logic && !conditionMet) {
          return true;
        }

        // For radio questions, check if an answer is selected
        if (type !== 'text') {
          const answer = _.get(answers, key);
          return answer && answer !== '';
        }

        // For text questions, always validate (treat all text fields as required)
        if (type === 'text' && required === true) {
          const answer = _.get(answers, key);
          return answer && answer.trim() !== '';
        }

        return true;
      });
    };
  }, [questions, answers]);

  const buildPayload = () => {
    const isAnonymous = !!_.get(initialFormData, 'is_anonymous');

    const payload = {
      is_anonymous: isAnonymous,
      customer_type: _.get(initialFormData, 'customer_type') || _.get(initialFormData, 'clientType') || 'Citizen',
      sex: _.get(initialFormData, 'sex') || _.get(answers, 'sex') || '',
      gender: _.get(initialFormData, 'gender') !== 'Other' 
        ? _.get(initialFormData, 'gender', '') 
        : _.get(initialFormData, 'gender_other', ''),
      age_range:
        _.get(initialFormData, 'age_range') ||
        _.get(initialFormData, 'age') ||
        _.get(answers, 'age') ||
        '',
      answers: [],
      gender_other: _.get(initialFormData, 'gender_other') || _.get(answers, 'gender_other') || '',
    };

    const allQuestions = buildSectionsFromQuestions(feedbackFormsCore)
      .flatMap((section) => section.questions || []);

    allQuestions.forEach((q) => {
      const id = _.get(q, 'id');
      if (!id) return;
      const key = questionKey(q);
      const type = _.get(q, 'question_type');
      const rawAnswer = _.get(answers, key);

      if (type === 'text') {
        if (!rawAnswer) return;
        payload.answers.push({
          question_id: id,
          answer_text: rawAnswer,
        });
        return;
      }

      const choices = normalizeChoices(_.get(q, 'choices'));

      const matchedChoice =
        choices.find((choice) => String(choice.value) === String(rawAnswer)) ||
        choices.find((choice) => String(choice.label) === String(rawAnswer));

      if (!matchedChoice) return;

      payload.answers.push({
        question_id: id,
        choice_id: matchedChoice.id,
        answer_value: matchedChoice.value, // add answer_value from choice_value
      });
    });

    return payload;
  };

  const goNext = () => {
    if (!isCurrentSectionValid()) {
      const missingRequiredFields = questions
        .filter((q) => {
          const key = questionKey(q);
          const type = _.get(q, 'question_type');
          const required = !!_.get(q, 'is_required');
          const logic = _.get(q, 'conditional_logic');
          const dependsOnId = _.get(logic, 'depends_on');
          const dependsKey = dependsOnId ? `q_${dependsOnId}` : null;
          const dependsOnAnswer = dependsKey ? _.get(answers, dependsKey) : undefined;
          const conditionMet = logic ? isConditionMet({ logic, dependsOnAnswer }) : true;

          if (logic && !conditionMet) return false;
          
          // Check if text field is empty
          if (type === 'text' && required === true) {
            const answer = _.get(answers, key);
            return !answer || answer.trim() === '';
          }
          
          // Check if radio field is empty
          if (type !== 'text') {
            const answer = _.get(answers, key);
            return !answer || answer === '';
          }
          return false;
        })
        .map((q) => questionText(q))
        .join('\n• ');

      alert(`Please fill out all required fields:\n• ${missingRequiredFields}`);
      return;
    }

    if (step < sections.length - 1) {
      setStep((s) => s + 1);
      return;
    }

    const payload = buildPayload();
    if (onSubmitted) {
      onSubmitted(payload);
      return;
    }
    onClose();
  };

  const goBack = () => {
    if (step > 0) {
      setStep((s) => s - 1);
    } else if (onBack) {
      onBack();
    }
  };

  const title = _.get(currentSection, 'title') || 'Feedback Form';
  const subtitle = _.get(currentSection, 'subtitle') || '';

  return (
    <>
      <div className="modal-body" style={{ padding: 18 }}>
        <div className="font-weight-bold" style={{ fontSize: 16, lineHeight: 1.2 }}>
          {title}
          {subtitle ? (
            <>
              <br />
              <span style={{ fontWeight: 700 }}>{subtitle}</span>
            </>
          ) : null}
        </div>

        <StepProgress step={2} />

        {!feedbackFormsCore ? (
          <div className="text-muted" style={{ fontSize: 13 }}>
            Loading questions…
          </div>
        ) : null}

        {feedbackFormsCore && !sections.length ? (
          <div className="text-muted" style={{ fontSize: 13 }}>
            No questions available.
          </div>
        ) : null}

        {questions.map((q) => {
          const key = questionKey(q);
          const label = questionText(q);
          const sub = questionSubText(q);
          const type = _.get(q, 'question_type');
          const required = !!_.get(q, 'is_required');

          const logic = _.get(q, 'conditional_logic');
          const dependsOnId = _.get(logic, 'depends_on');
          const dependsKey = dependsOnId ? `q_${dependsOnId}` : null;
          const dependsOnAnswer = dependsKey ? _.get(answers, dependsKey) : undefined;

          // If we don't have the answer yet, hide the conditional question
          if (logic && (dependsOnAnswer === undefined || dependsOnAnswer === null || dependsOnAnswer === '')) {
            return null;
          }

          const conditionMet = isConditionMet({ logic, dependsOnAnswer });
          const choices = normalizeChoices(_.get(q, 'choices'));

          const autoText = _.get(logic, 'auto_answers[0].answer_text', 'N/A');
          const autoChoice =
            choices.find(
              (choice) =>
                String(choice.label).trim().toLowerCase() ===
                String(autoText).trim().toLowerCase()
            ) || choices.find((choice) => String(choice.label).trim().toLowerCase() === 'n/a');
          const autoValue = autoChoice ? autoChoice.value : autoText;
          const selectedValue = String(
            logic && !conditionMet ? autoValue : _.get(answers, key) ?? ''
          );

          return (
            <div key={key} className="mb-3">
              <div style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.35 }}>
                {label} {required ? <span style={{ color: '#D32F2F' }}>*</span> : null}
              </div>
              {sub ? (
                <div className="text-muted mt-1" style={{ fontSize: 11, lineHeight: 1.4 }}>
                  {sub}
                </div>
              ) : null}

              {type === 'text' ? (
                <textarea
                  className="form-control mt-2"
                  name={key}
                  value={selectedValue || ''}
                  onChange={onChangeText}
                  rows={3}
                  style={{ fontSize: 13, borderRadius: 8 }}
                  disabled={logic && !conditionMet}
                  required={required}
                />
              ) : (
                <div className="d-flex flex-column mt-2" style={{ gap: 10 }}>
                  {choices.map((o) => (
                    <label
                      key={`${key}-${String(o.id ?? o.value)}`}
                      className="d-flex align-items-center mb-0"
                      style={{ gap: 10, opacity: logic && !conditionMet ? 0.6 : 1 }}
                    >
                      <input
                        type="radio"
                        name={key}
                        style={{ width: 20, height: 20 }}
                        onChange={onChangeRadio}
                        checked={selectedValue === o.value}
                        value={o.value}
                        disabled={logic && !conditionMet}
                      />
                      <span style={{ fontSize: 12 }}>{o.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="modal-footer gap-2">
        <button
          type="button"
          className="btn btn-dark btn-block"
          style={{ padding: '12px 16px', borderRadius: 8, fontWeight: 600 }}
          onClick={goNext}
          disabled={feedbackFormsCore && sections.length === 0 ? true : !isCurrentSectionValid()}
        >
          {step < sections.length - 1 ? 'Next' : 'Submit Feedback'}
        </button>
        <button
          type="button"
          className="btn btn-dark btn-block"
          style={{ padding: '12px 16px', borderRadius: 8, fontWeight: 600 }}
          onClick={goBack}
        >
          Back
        </button>
        <button
          type="button"
          className="btn btn-block btn-link text-dark"
          style={{ fontWeight: 600, textDecoration: 'none' }}
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </>
  );
}

FeedbackQuestionsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  initialFormData: PropTypes.instanceOf(Object),
  getFeedbackFormsCore: PropTypes.func.isRequired,
  feedbackFormsCore: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.instanceOf(Array)]),
  onSubmitted: PropTypes.func,
  onBack: PropTypes.func,
};

FeedbackQuestionsModal.defaultProps = {
  initialFormData: null,
  feedbackFormsCore: null,
  onSubmitted: null,
  onBack: null,
};

const mapStateToProps = ({ api }) => ({
  feedbackFormsCore: _.get(api, `${c.GET_FEEDBACK_FORMS_CORE}.item`) || null,
});

const enhance = _.flowRight([connect(mapStateToProps, actions)]);

export default enhance(FeedbackQuestionsModal);

